import React, { useState } from 'react';
import { format } from 'date-fns';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

export const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, onClose }) => {
  const now = new Date();
  const initialHour = value ? parseInt(value.split(':')[0]) : now.getHours();
  const initialMinute = value ? parseInt(value.split(':')[1]) : now.getMinutes();
  
  const is12Hour = initialHour % 12 || 12;
  const isPM = initialHour >= 12;

  const [hour, setHour] = useState(is12Hour);
  const [minute, setMinute] = useState(initialMinute);
  const [period, setPeriod] = useState(isPM ? 'PM' : 'AM');

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleOk = () => {
    let h24 = hour;
    if (period === 'PM' && hour !== 12) h24 += 12;
    if (period === 'AM' && hour === 12) h24 = 0;
    
    const timeString = `${h24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    onChange(timeString);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-sm rounded-t-xl sm:rounded-xl shadow-xl">
        <div className="p-4 border-b">
          <div className="text-center">
            <div className="text-3xl font-bold mb-1">
              {format(new Date(2024, 0, 1, hour === 12 ? (period === 'AM' ? 0 : 12) : (period === 'PM' ? hour + 12 : hour), minute), 'h:mm')}
            </div>
            <div className="text-lg text-gray-500">{period}</div>
          </div>
        </div>

        <div className="p-4 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hour</label>
              <select
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value))}
                className="w-full h-12 border rounded-lg py-2 px-3 text-lg"
              >
                {hours.map((h) => (
                  <option key={h} value={h}>
                    {h.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minute</label>
              <select
                value={minute}
                onChange={(e) => setMinute(parseInt(e.target.value))}
                className="w-full h-12 border rounded-lg py-2 px-3 text-lg"
              >
                {minutes.map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setPeriod('AM')}
              className={`h-12 rounded-lg text-lg font-medium ${
                period === 'AM'
                  ? 'bg-blue-500 text-white'
                  : 'border hover:bg-gray-50'
              }`}
            >
              AM
            </button>
            <button
              type="button"
              onClick={() => setPeriod('PM')}
              className={`h-12 rounded-lg text-lg font-medium ${
                period === 'PM'
                  ? 'bg-blue-500 text-white'
                  : 'border hover:bg-gray-50'
              }`}
            >
              PM
            </button>
          </div>
        </div>

        <div className="p-4 border-t grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-12 text-gray-700 font-medium rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleOk}
            className="w-full h-12 text-white font-medium bg-blue-500 rounded-lg hover:bg-blue-600"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimePicker;